import type { QcResultRead } from "../api/types";
import { formatNumber } from "../lib/format";
import { InfoTip } from "./Tooltip";

// Richer per-video stat tiles, grouped: Trajectory, IMU, Frame timing, Sync. Values come
// from QC columns + the extra metrics_json fields stera emits (gyro peak, bbox, frame dt…).
// metrics_json is loosely typed, so we read it through small defensive accessors.

interface Props {
  qc: QcResultRead;
}

function num(mj: Record<string, unknown>, key: string): number | null {
  const v = mj[key];
  return typeof v === "number" && Number.isFinite(v) ? v : null;
}

function numArray(mj: Record<string, unknown>, key: string): number[] | null {
  const v = mj[key];
  return Array.isArray(v) && v.every((x) => typeof x === "number") ? (v as number[]) : null;
}

function fmt(v: number | null, digits: number, unit?: string): string {
  if (v == null) return "—";
  const s = formatNumber(v, digits);
  return unit ? `${s} ${unit}` : s;
}

type Tile = { label: string; value: string; tip?: string };

function Group({ title, tiles }: { title: string; tiles: Tile[] }) {
  const shown = tiles.filter((t) => t.value !== "—");
  if (shown.length === 0) return null;
  return (
    <div className="vstat-group">
      <span className="vstat-group-title">{title}</span>
      <div className="vstat-tiles">
        {shown.map((t) => (
          <div className="vstat-tile" key={t.label}>
            <span className="vstat-value">{t.value}</span>
            <span className="vstat-label">
              {t.label}
              {t.tip && <InfoTip content={t.tip} />}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function VideoStats({ qc }: Props) {
  const mj = qc.metrics_json ?? {};
  const bbox = numArray(mj, "trajectory_bbox_m");
  const bboxStr = bbox && bbox.length === 3 ? bbox.map((x) => x.toFixed(2)).join(" × ") : null;

  const trajectory: Tile[] = [
    {
      label: "Path length",
      value: fmt(qc.trajectory_path_m, 2, "m"),
      tip: "Total distance the camera travelled over the take.",
    },
    {
      label: "Workspace",
      value: bboxStr ? `${bboxStr} m` : "—",
      tip: "Bounding-box extents (X × Y × Z) the camera moved within.",
    },
    {
      label: "Max reach",
      value: fmt(num(mj, "trajectory_max_displacement_m"), 2, "m"),
      tip: "Farthest the camera got from its start position.",
    },
    {
      label: "Mean speed",
      value: fmt(num(mj, "trajectory_mean_speed_mps"), 2, "m/s"),
      tip: "Average camera translation speed across the take.",
    },
  ];

  const imu: Tile[] = [
    {
      label: "Rate",
      value: qc.imu_rate_hz != null ? `${formatNumber(qc.imu_rate_hz, 0)} Hz` : "—",
      tip: "Accelerometer/gyro sample rate (rig targets ~500 Hz).",
    },
    {
      label: "Gravity |a|",
      value: fmt(qc.imu_gravity_mean, 2, "m/s²"),
      tip: "Mean accel magnitude at rest; should sit near 9.81 m/s².",
    },
    {
      label: "Gyro peak",
      value: fmt(num(mj, "imu_gyro_peak_dps"), 0, "°/s"),
      tip: "Fastest angular velocity seen — high = quick rotations.",
    },
    {
      label: "Stationary",
      value: num(mj, "imu_stationary_pct") != null ? `${formatNumber(num(mj, "imu_stationary_pct"), 0)}%` : "—",
      tip: "Share of the take the rig was effectively still.",
    },
  ];

  const timing: Tile[] = [
    {
      label: "FPS (mean)",
      value: fmt(qc.frame_fps_mean, 1),
      tip: "Mean RGB frame rate (target ~30 fps).",
    },
    {
      label: "Frame Δt",
      value: num(mj, "frame_dt_ms_mean") != null ? `${formatNumber(num(mj, "frame_dt_ms_mean"), 1)} ms` : "—",
      tip: "Mean inter-frame interval (±std). Spikes mean stutter.",
    },
    {
      label: "Worst Δt",
      value: num(mj, "frame_dt_ms_max") != null ? `${formatNumber(num(mj, "frame_dt_ms_max"), 0)} ms` : "—",
      tip: "Longest gap between two consecutive frames.",
    },
    {
      label: "Long gaps",
      value: fmt(qc.frame_long_gaps, 0),
      tip: "Count of dropped/late frame gaps (each costs score).",
    },
  ];

  const pct = (key: string): string => {
    const v = num(mj, key);
    return v != null ? `${formatNumber(v, 1)}%` : "—";
  };
  const imageQuality: Tile[] = [
    {
      label: "Sharpness",
      value: fmt(num(mj, "iq_sharpness_median"), 0),
      tip: "Median Laplacian variance over a frame sample; higher = crisper. Below ~40 reads as blurry.",
    },
    {
      label: "Low quality",
      value: pct("iq_low_quality_pct"),
      tip: "Share of sampled frames that are blurry or badly exposed.",
    },
    {
      label: "Overexposed",
      value: pct("iq_overexposed_pct"),
      tip: "Frames with >5% blown-out bright pixels.",
    },
    {
      label: "Underexposed",
      value: pct("iq_underexposed_pct"),
      tip: "Frames mostly crushed to black.",
    },
    {
      label: "Sampled",
      value: fmt(num(mj, "iq_frames_sampled"), 0),
      tip: "Frames analysed for these metrics (evenly spread across the take).",
    },
  ];

  const sync: Tile[] = [
    {
      label: "Worst IMU sync",
      value: fmt(qc.sync_worst_imu_ms, 2, "ms"),
      tip: "Largest RGB↔IMU timestamp gap. Smaller = tighter fusion.",
    },
    {
      label: "Worst pose sync",
      value: fmt(qc.sync_worst_pose_ms, 2, "ms"),
      tip: "Largest RGB↔pose timestamp gap (target ≤50 ms).",
    },
  ];

  return (
    <section className="card vstat-card">
      <div className="card-head">
        <h3>Capture detail</h3>
      </div>
      <div className="vstat-groups">
        <Group title="Trajectory" tiles={trajectory} />
        <Group title="IMU" tiles={imu} />
        <Group title="Frame timing" tiles={timing} />
        <Group title="Image quality" tiles={imageQuality} />
        <Group title="Sync" tiles={sync} />
      </div>
    </section>
  );
}
